import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { Outlet, useLocation } from 'react-router-dom';

import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { AppRoutes } from '../routes';

import routes from './routes';
import { useLaunchDarkly } from '../launchdarkly';
import { NavigationBar } from './navbar';
import { fromLayout } from '../store';
import { Sidebar } from './sidebar';
import { AccountProvider } from '../account';
import { LoadingProgress } from './LoadingProgress';
import { useTrackCookie } from './hook/useTrackCookie';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    width: '100vw',
  },
  headerSection: {
    zIndex: 10,
    height: theme.spacing(7.5),
  },
  content: {
    flexGrow: 1,
    flexDirection: 'column',
    width: '100%',
    display: 'flex',
    overflow: 'auto',
    height: '100%',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  bannerContainer: {
    flexGrow: 1,
    flexDirection: 'column',
    display: 'flex',
    overflowY: 'hidden',
  },
  warningBanner: {
    paddingTop: theme.spacing(7.5),
  },
  drawer: {
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
    },
  },
  headerSectionContainer: {
    zIndex: 1201,
    backgroundColor: '#1847ca',
    width: '100%',
    height: theme.spacing(7.5),
  },
  contentHidden: {
    visibility: 'hidden',
  },
  contentShow: {
    visibility: 'visible',
  },
}));

export const Layout = () => {
  const classes = useStyles();
  const isLoading = useSelector(fromLayout.selectIsLoading);
  const showNavbar = useSelector(fromLayout.selectShowNavbar);
  const { flags } = useLaunchDarkly();
  const navBarEnabled = flags?.navigationBarEnabled && showNavbar;
  const [sidebarWidth, setSidebarWidth] = useState<number>(0);
  const { pathname } = useLocation();
  const currentRoute = routes.find(route => pathname === route.path);

  // set tracking query param to cookie
  useTrackCookie();

  const sidebarRef = useCallback(node => {
    let observer: ResizeObserver;
    if (node !== null) {
      observer = new ResizeObserver(() => {
        setSidebarWidth(node.clientWidth);
      });
      observer.observe(node);
    }

    // Cleanup function
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const filteredRoutes = routes.filter(route => route.version === currentRoute?.version);

  const renderNavigationBar = () => (
    <Box id="katalon-purchase-ui-header" className={classes.headerSection}>
      <NavigationBar routes={filteredRoutes} />
    </Box>
  );

  return (
    <AccountProvider>
      <Box className={classes.root}>
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
        >
          {/* Navigation Bar */}
          {navBarEnabled && renderNavigationBar()}
          {isLoading && (<LoadingProgress fullscreen />)}
          <Box
            display="flex"
            flexDirection="row"
            height={theme => (navBarEnabled ? `calc(100% - ${theme.spacing(7.5)})` : '100%')}
            width="100%"
            justifyContent="space-between"
          >
            {/* Sidebar */}
            <Box
              id="katalon-purchase-ui-sidebar"
              ref={sidebarRef}
            >
              <Sidebar
                filteredRoutes={filteredRoutes}
              />
            </Box>

            {/* Content */}
            <Box
              id="katalon-purchase-ui-body"
              display="flex"
              width={`calc(100% - ${sidebarWidth}px)`}
              sx={theme => ({
                height: `calc(100% - ${flags?.navigationBarEnabled ? theme.spacing(7.5) : 0})`,
              })}
            >
              <Box
                id="katalon-purchase-ui-container"
                className={classes.content}
              >
                <AppRoutes routes={filteredRoutes} />
              </Box>
            </Box>
          </Box>
          <div className={clsx({
            [classes.contentHidden]: isLoading,
            [classes.contentShow]: !isLoading,
          })}
          >
            <Outlet />
          </div>
          {/* Route outlet */}
        </Box>
      </Box>
    </AccountProvider>
  );
};
